import React, { useContext, useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import {
  Col,
  Container,
  Row,
  Form,
  Input,
  Label,
  Card,
  CardBody,
  Button,
  FormFeedback,
  CardTitle,
} from "reactstrap"
import * as Yup from "yup"
import Select from "react-select"
import { useFormik } from "formik"
import { classApi } from "apis/ClassListApi"
import { classGroupApi } from "apis/ClassGroupApi"
import Swal from "sweetalert2"
import { CircularApi } from "apis/CircularAPI"
import config from "config/config"
import { SessionContext } from "context/sessionContext"
import Loading from "components/Loading"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"

const EditCircular = props => {
  document.title = "Edit Circular"
  const [user, setUserLogged] = useState(
    JSON.parse(localStorage.getItem("User"))
  )
  const [circular, setCircular] = useState([])
  const [classList, setClassList] = useState([])
  const [AllClassGroup, setAllClassGroup] = useState([])
  const [loading, setloading] = useState(false)
  const { Session } = useContext(SessionContext)
  let session = Session || sessionStorage.getItem("SessionId")
  const { id } = useParams()
  const [selectedFiles, setSelectedFiles] = useState([])
  const [fileError, setFileError] = useState("")
  const [existingFiles, setExistingFiles] = useState([])
  const [removeAttachmentIds, setRemoveAttachmentIds] = useState([])

  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB in bytes
  const MAX_FILES_COUNT = 5

  // validation
  const validation = useFormik({
    enableReinitialize: true, // Keep this true for edit page to load initial data

    initialValues: {
      title: "",
      description: "",
      st_class: [],
      send_by: [],
      notice_file: "",
      circular_end_date: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Please Enter Your Title"),
      st_class: Yup.array().min(1, "Please Enter Your Class"),
      send_by: Yup.array().min(1, "This is required"),
    }),
    onSubmit: values => {
      setloading(true)
      const data = new FormData()
      if (selectedFiles && selectedFiles.length > 0) {
        selectedFiles.forEach((file, index) => {
          data.append(`notice_file_${index + 1}`, file)
        })
      }
      if (removeAttachmentIds && removeAttachmentIds.length > 0) {
        data.append(
          "remove_attachment_ids",
          JSON.stringify(removeAttachmentIds)
        )
      }
      data.append("title", values.title)
      data.append("description", values.description)
      if (user === "Admin") {
        // send array of class group ids
        data.append("groupofclass_ids", JSON.stringify(values.st_class || []))
        // also expand groups to send individual classes
        const classes = []
        ;(values.st_class || []).forEach(groupId => {
          const group = AllClassGroup.find(g => g.value === groupId)
          if (group && group.st_class) {
            try {
              const groupClasses = JSON.parse(group.st_class)
              groupClasses.forEach(c => classes.push(c))
            } catch (e) {
              // ignore
            }
          }
        })
        data.append("st_class", JSON.stringify(classes))
      } else {
        data.append("st_class", JSON.stringify(values.st_class))
      }
      data.append("send_by", JSON.stringify(values.send_by))
      data.append("circular_id", id)
      const currentSession = Session || sessionStorage.getItem("SessionId") || session
      data.append("session_id", currentSession)
      if (values.circular_end_date) {
        data.append("circular_end_date", values.circular_end_date)
      }
      CircularApi.updateCircular(data)
        .then(res => {
          if (res.data.status === 200) {
            setloading(false)
            Swal.fire({
              text: res.data.msg,
              icon: "success",
              imageAlt: "success image",
            }).then(result => {
              if (result.isConfirmed) {
                window.location.href = "/circular"
              }
            })
          }
        })
        .catch(err => {
          console.log(err)
        })
    },
  })

  let cid = {
    circular_id: id,
  }

  useEffect(() => {
    CircularApi.getSingleCircular(cid)
      .then(res => {
        const circularData = res.data.circular[0]
        setCircular(circularData)
        setExistingFiles(parseCircularFiles(circularData))

        // Set Formik values directly
        validation.setFieldValue("title", circularData.title)
        validation.setFieldValue("description", circularData.description)
        // If circular was created using class groups, prefer those ids
        if (
          circularData.groupofclass_ids &&
          Array.isArray(circularData.groupofclass_ids)
        ) {
          validation.setFieldValue("st_class", circularData.groupofclass_ids)
        } else if (circularData.st_class) {
          validation.setFieldValue(
            "st_class",
            JSON.parse(circularData.st_class)
          )
        }
        validation.setFieldValue(
          "send_by",
          circularData.send_by ? JSON.parse(circularData.send_by) : []
        )
        validation.setFieldValue(
          "circular_end_date",
          circularData.circular_end_date || ""
        )
      })
      .catch(err => {
        console.log(err)
      })
  }, [id])

  const parseCircularFiles = cir => {
    if (!cir) return []

    // 1. Check array fields (notice_file, attachments, notice_files, files, circular_files, pdf_files)
    const arrayFields = [
      cir.notice_file,
      cir.attachments,
      cir.notice_files,
      cir.files,
      cir.circular_files,
      cir.pdf_files,
    ]
    for (const arr of arrayFields) {
      if (Array.isArray(arr) && arr.length > 0) {
        return arr
          .map((item, idx) => {
            if (typeof item === "string") {
              return { id: null, file_name: getFileName(item, idx), url: item }
            }
            if (typeof item === "object" && item !== null) {
              const url =
                item.url || item.file || item.notice_file || item.path || ""
              const name = item.file_name || item.name || getFileName(url, idx)
              return {
                id: item.id || null,
                file_name: name,
                url: url,
              }
            }
            return null
          })
          .filter(Boolean)
      }
    }

    // 2. Check numbered fields: notice_file_1, notice_file_2, notice_file_3, notice_file_4 (and notice_file1, notice_file2...)
    const numberedFiles = []
    for (let i = 1; i <= 10; i++) {
      const val = cir[`notice_file_${i}`] || cir[`notice_file${i}`]
      if (val) {
        if (typeof val === "string" && val.trim()) {
          numberedFiles.push({
            id: null,
            file_name: getFileName(val.trim(), i - 1),
            url: val.trim(),
          })
        } else if (typeof val === "object" && val !== null) {
          const url =
            val.url || val.file || val.notice_file || val.path || ""
          numberedFiles.push({
            id: val.id || null,
            file_name: val.file_name || val.name || getFileName(url, i - 1),
            url: url,
          })
        }
      }
    }
    if (numberedFiles.length > 0) {
      return numberedFiles
    }

    const fileVal = cir.notice_file
    if (!fileVal) return []
    if (typeof fileVal === "string") {
      const trimmed = fileVal.trim()
      if (!trimmed) return []
      if (
        (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))
      ) {
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) {
            return parsed
              .map((item, idx) => {
                if (typeof item === "string") {
                  return { id: null, file_name: getFileName(item, idx), url: item }
                }
                if (typeof item === "object" && item !== null) {
                  const url =
                    item.url || item.file || item.notice_file || item.path || ""
                  const name = item.file_name || item.name || getFileName(url, idx)
                  return {
                    id: item.id || null,
                    file_name: name,
                    url: url,
                  }
                }
                return null
              })
              .filter(Boolean)
          }
        } catch (e) {}
      }
      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map(f => f.trim())
          .filter(Boolean)
          .map((item, idx) => ({ id: null, file_name: getFileName(item, idx), url: item }))
      }
      return [{ id: null, file_name: getFileName(trimmed, 0), url: trimmed }]
    }
    return []
  }

  const getFileUrl = filePath => {
    if (!filePath) return ""
    const pathStr =
      typeof filePath === "object" && filePath !== null
        ? filePath.url || filePath.file || ""
        : filePath
    if (!pathStr || typeof pathStr !== "string") return ""
    if (
      pathStr.startsWith("http://") ||
      pathStr.startsWith("https://") ||
      pathStr.startsWith("blob:") ||
      pathStr.startsWith("data:")
    ) {
      return pathStr
    }
    const cleanPath = pathStr.startsWith("/") ? pathStr.slice(1) : pathStr
    return `${config.BaseImageUrl}/${cleanPath}`
  }

  const getFileName = (filePath, index) => {
    if (!filePath) return `Document ${index !== undefined ? index + 1 : 1}`
    if (typeof filePath === "object" && filePath !== null && filePath.file_name) {
      return filePath.file_name
    }
    const pathStr =
      typeof filePath === "object" && filePath !== null
        ? filePath.url || filePath.file || ""
        : filePath
    if (!pathStr || typeof pathStr !== "string") {
      return `Document ${index !== undefined ? index + 1 : 1}`
    }
    try {
      const clean = pathStr.split("?")[0].split("#")[0]
      const parts = clean.split("/")
      const name = parts[parts.length - 1]
      return decodeURIComponent(name) || `Document ${index !== undefined ? index + 1 : 1}`
    } catch (e) {
      return `Document ${index !== undefined ? index + 1 : 1}`
    }
  }

  const downloadFile = (fileUrl, fileName) => {
    fetch(fileUrl)
      .then(response => {
        if (!response.ok) throw new Error("Network response error")
        return response.blob()
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = fileName || "circular-document.pdf"
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => {
        const a = document.createElement("a")
        a.href = fileUrl
        a.download = fileName || "circular-document.pdf"
        a.target = "_blank"
        document.body.appendChild(a)
        a.click()
        a.remove()
      })
  }

  const handleFileChange = e => {
    setFileError("")
    const incomingFiles = Array.from(e.target.files || [])
    if (incomingFiles.length === 0) return

    const totalCurrent = existingFiles.length + selectedFiles.length
    if (totalCurrent + incomingFiles.length > MAX_FILES_COUNT) {
      setFileError(
        `Total documents cannot exceed ${MAX_FILES_COUNT}. (${existingFiles.length} existing, ${selectedFiles.length} selected)`
      )
      e.target.value = ""
      return
    }

    const newFiles = []
    for (const file of incomingFiles) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`"${file.name}" exceeds the 5 MB limit.`)
        e.target.value = ""
        return
      }
      const duplicate = selectedFiles.some(
        f => f.name === file.name && f.size === file.size
      )
      if (!duplicate) {
        newFiles.push(file)
      }
    }

    const updated = [...selectedFiles, ...newFiles]
    setSelectedFiles(updated)
    validation.setFieldValue("notice_file", updated[0] || "")
    e.target.value = ""
  }

  const handleRemoveFile = index => {
    const updated = selectedFiles.filter((_, idx) => idx !== index)
    setSelectedFiles(updated)
    validation.setFieldValue("notice_file", updated[0] || "")
    setFileError("")
  }

  const handleRemoveExistingFile = (fileItem, indexToRemove) => {
    const fileName = getFileName(fileItem, indexToRemove)
    Swal.fire({
      title: "Remove Document?",
      text: `Are you sure you want to remove "${fileName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#74788d",
      confirmButtonText: "Yes, remove it!",
    }).then(result => {
      if (result.isConfirmed) {
        setExistingFiles(prev => prev.filter((_, idx) => idx !== indexToRemove))
        if (fileItem && fileItem.id) {
          setRemoveAttachmentIds(prev => [...prev, fileItem.id])
        }
        setFileError("")
      }
    })
  }

  useEffect(() => {
    classApi
      .getAllClass()
      .then(res => {
        setClassList(res.data.section_list)
      })
      .catch(err => {
        console.log(err)
      })
    // load class groups for admin
    classGroupApi.getClassGroup().then(res => {
      const groups =
        res.data &&
        res.data.grupofclass &&
        res.data.grupofclass.map(g => ({
          value: g.id,
          label: g.group_name,
          st_class: g.st_class,
        }))
      setAllClassGroup(groups || [])
    })
  }, [])

  const classListOption = classList
    ? classList.map(cl => {
        return {
          value: cl.UID,
          label: `${cl.st_class} ${cl.st_sec}`,
        }
      })
    : []

  const SendListOption = [
    { value: 1, label: "Teacher" },
    { value: 2, label: "Student" },
    { value: 3, label: "Parent" },
  ]

  const classhandleChange = selectedOption => {
    validation.setFieldValue("st_class", selectedOption)
  }

  const handleChange = selectedOption => {
    validation.setFieldValue("send_by", selectedOption)
  }

  const formClear = () => {
    validation.resetForm()
  }

  return (
    <React.Fragment>
      <style>{`
        /* Make disabled / previous-month dates more visible in flatpickr */
        .flatpickr-day.flatpickr-disabled,
        .flatpickr-day.flatpickr-disabled:hover,
        .flatpickr-day.prevMonthDay,
        .flatpickr-day.nextMonthDay {
          color: #74788d !important;
          opacity: 0.85 !important;
        }
      `}</style>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Circular" breadcrumbItem="Edit Circular" />
          <Row>
            <Col lg="12">
              <Card>
                {loading ? (
                  <Loading />
                ) : (
                  <CardBody>
                    <Form
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <Row>
                        <Col sm={12} lg={6}>
                          <div className="mb-2">
                            <Label htmlFor="title" className="my-control">
                              Title
                            </Label>
                            <Input
                              id="n_title"
                              name="title"
                              type="text"
                              className="form-control"
                              placeholder="Title"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.title}
                              invalid={
                                validation.touched.title &&
                                validation.errors.title
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.title &&
                            validation.errors.title ? (
                              <FormFeedback type="invalid">
                                {validation.errors.title}
                              </FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3">
                            {user === "Admin" ? (
                              <>
                                <Label className="control-label my-control">
                                  Class Group
                                </Label>
                                <Select
                                  name="st_class"
                                  className="basic-multi-select"
                                  classNamePrefix="select2-selection"
                                  placeholder="Select Class Group..."
                                  title="Class Group"
                                  options={AllClassGroup}
                                  isMulti
                                  value={AllClassGroup.filter(opt => {
                                    let stClass = validation.values.st_class

                                    // If stringified JSON
                                    if (typeof stClass === "string") {
                                      try {
                                        stClass = JSON.parse(stClass)
                                      } catch (e) {
                                        stClass = []
                                      }
                                    }

                                    // Ensure array
                                    if (!Array.isArray(stClass)) {
                                      stClass = []
                                    }

                                    return stClass.includes(opt.value)
                                  })}
                                  onChange={selected => {
                                    const ids = selected
                                      ? selected.map(s => s.value)
                                      : []
                                    validation.setFieldValue("st_class", ids)
                                  }}
                                  onBlur={validation.handleBlur}
                                  invalid={
                                    validation.touched.st_class &&
                                    validation.errors.st_class
                                      ? true
                                      : false
                                  }
                                />
                              </>
                            ) : (
                              <>
                                <Label className="control-label my-control">
                                  Class
                                </Label>
                                <Select
                                  name="st_class"
                                  className="basic-multi-select"
                                  classNamePrefix="select2-selection"
                                  placeholder="Select Class..."
                                  title="Class"
                                  options={classListOption}
                                  isMulti
                                  value={validation.values.st_class}
                                  onChange={classhandleChange}
                                  onBlur={validation.handleBlur}
                                  invalid={
                                    validation.touched.st_class &&
                                    validation.errors.st_class
                                      ? true
                                      : false
                                  }
                                />
                              </>
                            )}
                            {validation.touched.st_class &&
                            validation.errors.st_class ? (
                              <div
                                style={{
                                  color: "#f46a6a",
                                  fontSize: "11px",
                                  marginTop: "3px",
                                }}
                              >
                                This is required
                              </div>
                            ) : null}
                          </div>

                          <div className="mb-3">
                            <Label className="control-label my-control">
                              To send
                            </Label>
                            <Select
                              name="send_by"
                              classNamePrefix="select2-selection"
                              placeholder="Select..."
                              title="To send"
                              options={SendListOption}
                              isMulti
                              value={validation.values.send_by}
                              onChange={handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.send_by &&
                                validation.errors.send_by
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.send_by &&
                            validation.errors.send_by ? (
                              <div
                                style={{
                                  color: "#f46a6a",
                                  fontSize: "11px",
                                  marginTop: "3px",
                                }}
                              >
                                This is required
                              </div>
                            ) : null}
                          </div>

                          {/* Expiry Date */}
                          <div className="mb-3">
                            <Label className="form-label">Expiry Date</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Select expiry date"
                              value={validation.values.circular_end_date}
                              options={{
                                dateFormat: "Y-m-d",
                                altInput: true,
                                altFormat: "d M Y",
                                minDate: "today",
                              }}
                              onChange={([date]) => {
                                if (date) {
                                  // Format using local date parts to avoid UTC
                                  // shifting the day back (e.g. selecting the 31st
                                  // becoming the 30th in IST).
                                  const yyyy = date.getFullYear()
                                  const mm = String(date.getMonth() + 1).padStart(2, "0")
                                  const dd = String(date.getDate()).padStart(2, "0")
                                  const formatted = `${yyyy}-${mm}-${dd}`
                                  validation.setFieldValue("circular_end_date", formatted)
                                }
                              }}
                            />
                          </div>
                        </Col>
                        <Col sm={12} lg={6}>
                          <div className="mb-3">
                            <Label htmlFor="Description">Description</Label>
                            <Input
                              id="n_title"
                              name="description"
                              rows="5"
                              type="textarea"
                              className="form-control"
                              placeholder="Write here your Description"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.description}
                              invalid={
                                validation.touched.description &&
                                validation.errors.description
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.description &&
                            validation.errors.description ? (
                              <FormFeedback type="invalid">
                                {validation.errors.description}
                              </FormFeedback>
                            ) : null}
                          </div>
                          {/* File Upload (Up to 5 Files) */}
                          <div className="mt-0">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <Label className="control-label mb-0" htmlFor="edit-circular-files">
                                Upload New Documents / PDFs
                              </Label>
                              <span className="badge bg-light text-primary font-size-11">
                                {existingFiles.length + selectedFiles.length} / {MAX_FILES_COUNT} total
                              </span>
                            </div>
                            <div className="input-group">
                              <Input
                                name="notice_file"
                                type="file"
                                multiple
                                accept=".pdf,application/pdf,image/png,image/jpeg,image/jpg"
                                className="form-control"
                                id="edit-circular-files"
                                disabled={existingFiles.length + selectedFiles.length >= MAX_FILES_COUNT}
                                onChange={handleFileChange}
                              />
                            </div>
                            <small className="text-muted d-block mt-1">
                              Supported formats: PDF, PNG, JPG (Max 5MB each, up to {MAX_FILES_COUNT} total documents)
                            </small>
                            {fileError && (
                              <div style={{ color: "red", fontSize: "11px", marginTop: "3px" }}>
                                <i className="mdi mdi-alert-circle-outline me-1" />
                                {fileError}
                              </div>
                            )}

                            {selectedFiles.length > 0 && (
                              <div className="mt-2 border rounded p-2 bg-light">
                                <div className="font-size-12 fw-bold text-muted mb-2">
                                  New Files to Upload ({selectedFiles.length}):
                                </div>
                                <div className="d-flex flex-column gap-1">
                                  {selectedFiles.map((file, idx) => {
                                    const isPdf =
                                      file.type === "application/pdf" ||
                                      file.name.toLowerCase().endsWith(".pdf")
                                    const sizeKb = (file.size / 1024).toFixed(1)
                                    const sizeMb = (file.size / (1024 * 1024)).toFixed(2)
                                    const sizeText =
                                      file.size > 1024 * 1024 ? `${sizeMb} MB` : `${sizeKb} KB`

                                    return (
                                      <div
                                        key={idx}
                                        className="d-flex align-items-center justify-content-between bg-white px-2 py-1 rounded border border-light"
                                      >
                                        <div className="d-flex align-items-center text-truncate me-2">
                                          {isPdf ? (
                                            <i className="mdi mdi-file-pdf-outline text-danger font-size-20 me-2" />
                                          ) : (
                                            <i className="mdi mdi-file-image-outline text-success font-size-20 me-2" />
                                          )}
                                          <span
                                            className="text-truncate font-size-12"
                                            title={file.name}
                                          >
                                            {file.name}
                                          </span>
                                          <span className="text-muted font-size-11 ms-2">
                                            ({sizeText})
                                          </span>
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-link text-danger p-0"
                                          onClick={() => handleRemoveFile(idx)}
                                          title="Remove file"
                                        >
                                          <i className="mdi mdi-trash-can-outline font-size-16" />
                                        </button>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Existing Uploaded Files */}
                          {existingFiles.length > 0 && (
                            <div className="position-relative my-3 border rounded p-3 bg-light">
                              <h6 className="font-size-13 mb-2 text-primary">
                                <i className="mdi mdi-file-check-outline me-1"></i>
                                Existing Attached Documents ({existingFiles.length}):
                              </h6>
                              <div className="d-flex flex-column gap-2">
                                {existingFiles.map((file, idx) => {
                                  const fullUrl = getFileUrl(file)
                                  const fileName = getFileName(file, idx)
                                  const fileStr = (
                                    typeof file === "string"
                                      ? file
                                      : (file && (file.file_name || file.url || "")) || ""
                                  ).toLowerCase()
                                  const isPdf =
                                    fileStr.endsWith(".pdf") ||
                                    fileStr.includes(".pdf") ||
                                    fileName.toLowerCase().endsWith(".pdf")

                                  return (
                                    <div
                                      key={idx}
                                      className="d-flex align-items-center justify-content-between bg-white p-2 rounded border"
                                    >
                                      <div className="d-flex align-items-center text-truncate me-2">
                                        {isPdf ? (
                                          <i className="mdi mdi-file-pdf-outline text-danger font-size-22 me-2" />
                                        ) : (
                                          <i className="mdi mdi-file-image-outline text-success font-size-22 me-2" />
                                        )}
                                        <span
                                          className="text-truncate font-size-12"
                                          title={fileName}
                                          style={{ maxWidth: "200px" }}
                                        >
                                          {fileName}
                                        </span>
                                      </div>
                                      <div className="d-flex gap-2">
                                        <a
                                          href={fullUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn btn-sm btn-outline-primary waves-effect"
                                          title="View in new tab"
                                        >
                                          <i className="mdi mdi-eye-outline me-1" />
                                          View
                                        </a>
                                        {/* <button
                                          type="button"
                                          onClick={() => downloadFile(fullUrl, fileName)}
                                          className="btn btn-sm btn-outline-secondary waves-effect"
                                          title="Download"
                                        >
                                          <i className="bx bx-download me-1" />
                                          Download
                                        </button> */}
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveExistingFile(file, idx)}
                                          className="btn btn-sm btn-outline-danger waves-effect"
                                          title="Remove this document"
                                        >
                                          <i className="mdi mdi-trash-can-outline me-1" />
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {removeAttachmentIds.length > 0 && (
                            <div className="alert alert-warning py-2 px-3 font-size-12 mb-3 d-flex align-items-center rounded">
                              <i className="mdi mdi-alert-circle-outline font-size-18 me-2" />
                              <span>
                                <strong>{removeAttachmentIds.length}</strong> document(s) marked for removal. Click <strong>Update</strong> below to save changes.
                              </span>
                            </div>
                          )}
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col sm={12} className="d-flex align-items-end justify-content-end gap-2">
                          <Link
                            to="/circular"
                            className="btn btn-secondary waves-effect"
                          >
                            Back
                          </Link>
                          <Button
                            color="primary"
                            type="submit"
                            className="btn btn-primary waves-effect waves-light"
                          >
                            Update
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                )}
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default EditCircular
